import React, { useEffect, useState } from 'react';
import { Box, Button, Snackbar, Typography } from '@mui/material';
import api from '../../api';
import RegularInput from '../input/RegularInput';
import SettingsCard from './SettingsCard';

/**
 * Calculator v3 §8 — tenant operating signals for the keep-vs-outsource
 * recommendation. Three numbers the engine consumes:
 *
 *   currentCapacityUtilization (0–1) — how loaded the crew currently is
 *   avgRealizedMarginPerHour ($)     — cash value of one capacity hour
 *   flexibilityPreference (0–1)      — bias at the parity tie-break
 *
 * Editable inline; saves via PATCH /company/current. Hides silently if
 * Calc v3 isn't enabled (the BE returns the field but FE doesn't surface
 * the card on classic-calc tenants).
 */
const OperatingSignalsCardDefaults = {};
const OperatingSignalsCard: React.FC = function OperatingSignalsCard() {
  const [utilizationPct, setUtilizationPct] = useState<string>(''); // 0-100 in UI
  const [marginPerHour, setMarginPerHour] = useState<string>('');
  const [flexibilityPct, setFlexibilityPct] = useState<string>(''); // 0-100 in UI
  const [hidden, setHidden] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [snackMsg, setSnackMsg] = useState<string>('');

  const load = async () => {
    try {
      const company: any = await api.CompanyApi.getCurrentCompany();
      if (!company?.calculatorV3Enabled) {
        setHidden(true);
        return;
      }
      const util = company.currentCapacityUtilization;
      const amh = company.avgRealizedMarginPerHour;
      const flex = company.flexibilityPreference;
      setUtilizationPct(util != null ? String(Number(util) * 100) : '');
      setMarginPerHour(amh != null ? String(Number(amh)) : '');
      setFlexibilityPct(
        flex != null ? String(Number(flex) * 100) : '50'
      );
    } catch {
      setHidden(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const utilNum =
        utilizationPct === '' ? null : Number(utilizationPct) / 100;
      const amhNum = marginPerHour === '' ? null : Number(marginPerHour);
      const flexNum =
        flexibilityPct === '' ? 0.5 : Number(flexibilityPct) / 100;
      const payload: Record<string, unknown> = {
        flexibilityPreference: flexNum
      };
      if (utilNum !== null) payload.currentCapacityUtilization = utilNum;
      if (amhNum !== null) payload.avgRealizedMarginPerHour = amhNum;
      await api.CompanyApi.updateCurrentCompany('', payload as any);
      setSnackMsg('Operating signals saved.');
    } catch (err: any) {
      setSnackMsg(
        err?.response?.data?.message ?? err?.message ?? 'Save failed.'
      );
    } finally {
      setSnackOpen(true);
      setSaving(false);
    }
  };

  if (hidden) return null;

  return (
    <SettingsCard title="Keep vs. Outsource Operating Signals">
      <Typography color="secondary.500" sx={{ mb: 2 }} variant="body_200">
        These three signals feed the keep-vs-outsource recommendation. Leave
        utilization or avg margin/hr blank to skip the capacity-cost
        adjustment until you have a confident number.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { md: 'repeat(3, 1fr)', xs: '1fr' }
        }}
      >
        <Box>
          <Typography color="mainText.500" variant="primary-bold">
            Current crew utilization (%)
          </Typography>
          <Typography color="secondary.400" sx={{ mb: 1 }} variant="body_100">
            Above 60% the engine starts adding an opportunity-cost penalty
            to keep margin.
          </Typography>
          <RegularInput
            onChange={(e: any) => setUtilizationPct(e.target.value)}
            placeholder="e.g. 75"
            type="number"
            value={utilizationPct}
          />
        </Box>

        <Box>
          <Typography color="mainText.500" variant="primary-bold">
            Avg realized margin per hour ($)
          </Typography>
          <Typography color="secondary.400" sx={{ mb: 1 }} variant="body_100">
            Cash value of one billable crew hour across your active book.
            Used as the per-hour opportunity cost.
          </Typography>
          <RegularInput
            onChange={(e: any) => setMarginPerHour(e.target.value)}
            placeholder="e.g. 18"
            type="number"
            value={marginPerHour}
          />
        </Box>

        <Box>
          <Typography color="mainText.500" variant="primary-bold">
            Flexibility preference (%)
          </Typography>
          <Typography color="secondary.400" sx={{ mb: 1 }} variant="body_100">
            At margin parity: 0 = always use the crew, 100 = always preserve
            capacity. 50 = neutral default.
          </Typography>
          <RegularInput
            onChange={(e: any) => setFlexibilityPct(e.target.value)}
            placeholder="50"
            type="number"
            value={flexibilityPct}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button disabled={saving} onClick={save} variant="contained">
          {saving ? 'Saving…' : 'Save signals'}
        </Button>
      </Box>

      <Snackbar
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        autoHideDuration={3000}
        message={snackMsg}
        onClose={() => setSnackOpen(false)}
        open={snackOpen}
      />
    </SettingsCard>
  );
};
OperatingSignalsCard.defaultProps = OperatingSignalsCardDefaults;

export default OperatingSignalsCard;
